import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import Slider from 'react-slick';
import { useAppDispatch } from 'src/AppContext';
import Button from 'src/components/Button';
import { KY_KARAOKE_NAME } from 'src/constants/ky';
import { PRODUCT_CATEGORY, PRODUCT_REGION } from 'src/constants/product';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';
import { userProductsCreated } from 'src/services/api';
import { getProducts } from 'src/services/checkout';
import { getCurrency, getTranslateName } from 'src/services/util';
import { useUser } from 'src/UserContext';

export default function LoginComplete(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useCustomHistory();
  const { errorModal } = useModal();
  const { isInit, isLogin, userInfo } = useUser();
  const [products, setProducts] = useState<CheckoutProductData[]>([]);

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'hidden' });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'close' });
    dispatch({
      type: 'SET_GNB_RIGHT_CALLBACK',
      cb: () => {
        history.push('/');
      },
    });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
      dispatch({ type: 'RESET_GNB_RIGHT_CALLBACK' });
    };
  }, []);

  useEffect(() => {
    if (!isInit) return;
    if (isLogin) {
      userProductsCreated()
        .then(async (res) => {
          if (res.products.length === 0) {
            getProducts().then((products) => {
              const result = products.filter((product) => {
                if (product.category === PRODUCT_CATEGORY.KARAOKE) {
                  if (userInfo.country === PRODUCT_REGION.KR) {
                    return product.region === PRODUCT_REGION.KR;
                  } else {
                    return product.region === PRODUCT_REGION.GLOBAL;
                  }
                }
                return false;
              });
              setProducts(result);
            });
          }
        })
        .catch(errorModal);
    }
  }, [isInit, isLogin]);

  const onClickHome = useCallback((e) => {
    e.preventDefault();
    history.push('/');
  }, []);

  const onClickDetail = useCallback((e, id) => {
    e.preventDefault();
    history.push('/product/purchase/' + id);
  }, []);

  return (
    <>
      <div className="container mt-6">
        <div className="N_20_R">{t('login/complete/title')}</div>
        <div className="mt-7 N_14_R text-brown-grey">
          <Trans
            i18nKey="login/complete/subtitle"
            components={{
              span: <span className="N_14_M text-greyish-brown"></span>,
            }}
          ></Trans>
        </div>
        <div className="mt-4 N_14_R text-brown-grey">
          {t(products.length === 0 ? 'login/complete/message_1' : 'login/complete/not_product_message')}
        </div>
        <div className="mt-10 mb-10">
          <Button title={t('login/complete/home_button')} theme="sub" onClick={onClickHome}></Button>
        </div>
      </div>
      {products.length > 0 && (
        <div className={['bg-light-gray-1', 'py-6'].join(' ')}>
          <div className="container">
            <div className="N_14_M">
              <Trans
                i18nKey="login/complete/service_product"
                values={{
                  service: KY_KARAOKE_NAME,
                }}
              ></Trans>
            </div>
          </div>
          <div id="login-complete-list" className="mt-3.5 ml-2">
            <Slider variableWidth={true} infinite={false}>
              {products.map((product) => (
                <div key={product.id} className="min-w-18.25">
                  <div className="border border-ice-blue bg-white rounded py-5 px-6">
                    <div className="N_14_R text-greyish-brown">{product[getTranslateName()]}</div>
                    <div className="mt-1.5 flex justify-between items-center">
                      <div className="N_16_M text-black">
                        <NumberFormat
                          value={product.amount}
                          displayType={'text'}
                          thousandSeparator={true}
                          prefix={getCurrency(product.region)}
                        />
                      </div>
                      <a href="#" className="N_12_M underline text-bluey-purple" onClick={(e) => onClickDetail(e, product.id)}>
                        {t('login/complete/detail')}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
    </>
  );
}
